import {
  RiskLedgerViewSchema,
  type RiskActionOwnerFacet,
  type RiskLedgerView,
  type RiskNatureFacet,
  type RiskTags,
  type SystemFacet
} from "@automatry/schemas";

import { solutionDisplayName } from "../app/format";
import type { ProjectFixtureForDisplay } from "./projectCockpitAdapter";
import type { ApartmentSolutionForDisplay } from "./solutionViewAdapter";

type RiskSeverity = "info" | "warning" | "blocker";
type RiskResolutionFacet = "open" | "accepted" | "resolved" | "converted_to_rfi";
type OpenDecisionStage = "lane-a" | "lane-b" | "output";
type ViewStatus = "loading" | "ready" | "blocked" | "error";
type SolutionRisk = ApartmentSolutionForDisplay["risks"][number];
type SolutionJustification = ApartmentSolutionForDisplay["justifications"][number];
type ProjectScopeRow = ProjectFixtureForDisplay["scopeMatrixRows"][number];
export type RiskLedgerSectionId =
  | "risks"
  | "assumptions"
  | "clarifications"
  | "exclusions";

interface ProjectRiskSummary {
  id: string;
  type?: string;
  tags?: RiskTags;
  severity?: RiskSeverity;
  title?: string;
  status: string;
  blocksExport: boolean;
}

interface OpenDecisionForDisplay {
  id: string;
  stage: OpenDecisionStage;
  subjectRef?: string;
  message?: string;
}

interface RiskSourceRecord {
  id: string;
  apartmentTypeId?: string;
  type?: string;
  tags?: RiskTags;
  severity?: RiskSeverity;
  title?: string;
  plainEnglish?: string;
  status: string;
  blocksExport: boolean;
  zoneKind?: string;
  affectedKind?: string;
  affectedItemKind?: string;
  itemKind?: string;
  scope?: {
    level: string;
    refId?: string;
    label?: string;
  };
  recommendedActions?: readonly {
    action: string;
    owner: string;
    status: string;
  }[];
  sourceRefs?: readonly unknown[];
  ruleTraceRefs?: readonly string[];
  technicalTrace?: readonly unknown[];
}

export interface RiskLedgerActionDisplay {
  action: string;
  ownerLabel: string;
  statusLabel: string;
}

export interface RiskLedgerRiskFacetValues {
  severity: RiskSeverity;
  system?: SystemFacet;
  nature?: RiskNatureFacet;
  actionOwner?: RiskActionOwnerFacet;
  resolution: RiskResolutionFacet;
}

export interface RiskLedgerRiskDisplay {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  apartmentTypeLabels: string[];
  affectedLabel: string;
  title: string;
  message: string;
  severity: RiskSeverity;
  severityLabel: string;
  type: string;
  typeLabel: string;
  status: string;
  statusLabel: string;
  blocksExport: boolean;
  scopeLabel: string;
  sourceRefCount: number;
  ruleTraceRefCount: number;
  technicalTraceCount: number;
  actionCount: number;
  sourceItemCount: number;
  actionOptions: RiskLedgerActionDisplay[];
  facets: RiskLedgerRiskFacetValues;
  decisionId?: string;
  clarificationPrompt?: string;
  dataGaps: string[];
}

export interface RiskLedgerRiskGroup {
  id: RiskSeverity;
  label: string;
  count: number;
  accent: string;
  items: RiskLedgerRiskDisplay[];
}

export interface RiskLedgerAssumptionDisplay {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  apartmentTypeLabels: string[];
  affectedLabel: string;
  targetRef: string;
  targetLabel: string;
  text: string;
  sourceItemCount: number;
  sourceRefCount: number;
  ruleTraceRefCount: number;
  dataGaps: string[];
}

export interface RiskLedgerClarificationDisplay {
  id: string;
  stage: OpenDecisionStage;
  stageLabel: string;
  subjectRef: string;
  subjectRefs: string[];
  title: string;
  question: string;
  affectedLabel: string;
  sourceItemCount: number;
  linkedRiskId?: string;
  apartmentTypeLabel?: string;
  source: "openDecision" | "needsUserDecision";
  notYetProduced: string;
}

export interface RiskLedgerExclusionDisplay {
  id: string;
  title: string;
  message: string;
  status: "excluded" | "by_others";
  statusLabel: string;
  affectedLabel: string;
  sourceItemCount: number;
  sourceRefCount: number;
  ruleTraceRefCount: number;
}

export interface RiskLedgerSectionSummary {
  id: RiskLedgerSectionId;
  label: string;
  count: number;
  sourceItemCount: number;
  accent: string;
}

export interface RiskLedgerDisplayViewModel extends RiskLedgerView {
  risks: RiskLedgerRiskDisplay[];
  riskGroups: RiskLedgerRiskGroup[];
  assumptions: RiskLedgerAssumptionDisplay[];
  clarifications: RiskLedgerClarificationDisplay[];
  exclusions: RiskLedgerExclusionDisplay[];
  ledgerGroups: RiskLedgerSectionSummary[];
  dedupedRowCount: number;
  rawLedgerItemCount: number;
  severityCounts: Record<RiskSeverity, number>;
  clarificationStageCounts: Record<OpenDecisionStage, number>;
  generatedFrom: string;
}

const severityOrder: RiskSeverity[] = ["blocker", "warning", "info"];

const severityMeta: Record<RiskSeverity, { label: string; accent: string }> = {
  blocker: { label: "Blockers", accent: "#BC4A3C" },
  warning: { label: "Warnings", accent: "#A8823E" },
  info: { label: "Information", accent: "#3E6E9A" }
};

const riskResolutionValues = new Set<string>([
  "open",
  "accepted",
  "resolved",
  "converted_to_rfi"
]);

const resolutionFacet = (status: string): RiskResolutionFacet =>
  riskResolutionValues.has(status) ? (status as RiskResolutionFacet) : "open";

const riskFacetValues = (
  risk: RiskSourceRecord,
  severity: RiskSeverity
): RiskLedgerRiskFacetValues => {
  const system = risk.tags?.system;
  const nature = risk.tags?.nature;
  const actionOwner = risk.tags?.actionOwner;

  return {
    severity,
    ...(system ? { system } : {}),
    ...(nature ? { nature } : {}),
    ...(actionOwner ? { actionOwner } : {}),
    resolution: resolutionFacet(risk.status)
  };
};

const mergeRiskFacetValues = (
  left: RiskLedgerRiskFacetValues,
  right: RiskLedgerRiskFacetValues
): RiskLedgerRiskFacetValues => {
  const system = left.system ?? right.system;
  const nature = left.nature ?? right.nature;
  const actionOwner = left.actionOwner ?? right.actionOwner;

  return {
    severity: left.severity,
    ...(system ? { system } : {}),
    ...(nature ? { nature } : {}),
    ...(actionOwner ? { actionOwner } : {}),
    resolution: left.resolution
  };
};

const sectionMeta: Record<RiskLedgerSectionId, { label: string; accent: string }> = {
  risks: { label: "Risks", accent: "#BC4A3C" },
  assumptions: { label: "Assumptions", accent: "#5E6E82" },
  clarifications: { label: "Clarifications", accent: "#6A5E97" },
  exclusions: { label: "Exclusions", accent: "#B08A3E" }
};

const labelise = (value: string): string =>
  value
    .replace(/^carrington-/, "")
    .replace(/^type-/, "Type ")
    .replace(/^reqcand_/, "Requirement candidate ")
    .replace(/^risk-/, "")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const unique = <T>(values: readonly T[]): T[] => [...new Set(values)];

const clusterKey = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

const affectedLabel = ({
  labels,
  totalApartmentTypeCount
}: {
  labels: readonly string[];
  totalApartmentTypeCount: number;
}): string => {
  const uniqueLabels = unique(labels.filter(Boolean));

  if (uniqueLabels.length === 0) {
    return "Whole job";
  }

  if (totalApartmentTypeCount > 0 && uniqueLabels.length >= totalApartmentTypeCount) {
    return "All types";
  }

  if (uniqueLabels.length <= 2) {
    return uniqueLabels.join(" · ");
  }

  return `${uniqueLabels.slice(0, 2).join(" · ")} +${uniqueLabels.length - 2}`;
};

const mergeDataGaps = (left: readonly string[], right: readonly string[]): string[] =>
  unique([...left, ...right]);

const typeLabel = (type: string): string =>
  type === "needsUserDecision" ? "Needs your decision" : labelise(type);

const sourceDesignStrategyToken = ["DE", "SOPS"].join("");
const requestInfoToken = ["R", "FI"].join("");

const displayTextReplacements: readonly [RegExp, string][] = [
  [
    /\bcandidate confidence ([0-9.]+)\s*<\s*([0-9.]+)\b/gi,
    "requirement candidate confidence $1 is below the $2 approval threshold"
  ],
  [/\bvisible\s+needsUserDecision\b/g, "visible decision item"],
  [/\bneedsUserDecision\b/g, "needs your decision"],
  [/\bdefaultDisposition\b/g, "default answer"],
  [
    new RegExp(`\\b${sourceDesignStrategyToken}\\b`, "gi"),
    "design and operation strategy"
  ],
  [new RegExp(`\\b${requestInfoToken}s\\b`, "gi"), "requests for information"],
  [new RegExp(`\\b${requestInfoToken}\\b`, "gi"), "request for information"],
  [/\bbuilding\s+BMS\b/gi, "building management system"],
  [/\bBMS\b/gi, "building management system"],
  [/\bFCUs\b/gi, "fan-coil units"],
  [/\bFCU\b/gi, "fan-coil unit"],
  [/\bWUFH\b/gi, "wet underfloor heating"],
  [/\bEUFH\b/gi, "electric underfloor heating"],
  [/\bUFH\b/gi, "underfloor heating"],
  [/\bIO\b/gi, "input/output"],
  [
    /\bThe engine emitted a placeholder only and did not invent a SKU\./gi,
    "A placeholder has been kept until the final product is selected."
  ],
  [/\bRisk message not emitted by the engine\./gi, "Risk message is not available yet."]
];

const humaniseAuthoredText = (value: string): string => {
  const withoutNamedConfirmation = value
    .replace(
      /\buntil\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+confirms\s+or\s+overrides\s+it\b/g,
      "until confirmed"
    )
    .replace(
      /\buntil\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+confirms\s+the\s+([^?.;,]+)/g,
      "until the $1 is confirmed"
    )
    .replace(
      /\buntil\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+confirms\b/g,
      "until confirmed"
    )
    .replace(
      /\bfor\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+to\s+confirm\b/g,
      "for confirmation"
    );

  return displayTextReplacements
    .reduce(
      (current, [pattern, replacement]) => current.replace(pattern, replacement),
      withoutNamedConfirmation
    )
    .replace(/\s+/g, " ")
    .trim();
};

const stageLabel = (stage: OpenDecisionStage): string => {
  if (stage === "lane-a") {
    return "Initial review";
  }

  if (stage === "lane-b") {
    return "Specification review";
  }

  return "Final output";
};

const viewStatus = (
  status: ProjectFixtureForDisplay["project"]["outputBundle"]["status"]
): ViewStatus => (status === "ready" ? "ready" : "blocked");

const riskRecordFromSolution = ({
  solution,
  risk
}: {
  solution: ApartmentSolutionForDisplay;
  risk: SolutionRisk;
}): RiskSourceRecord => ({
  ...risk,
  apartmentTypeId: solution.apartmentTypeId
});

const riskRecordFromSummary = (risk: ProjectRiskSummary): RiskSourceRecord => ({
  ...risk,
  severity: risk.severity ?? "warning"
});

const actionOptions = (
  actions: readonly {
    action: string;
    owner: string;
    status: string;
  }[]
): RiskLedgerActionDisplay[] =>
  actions.map((action) => ({
    action: humaniseAuthoredText(action.action),
    ownerLabel: labelise(action.owner),
    statusLabel: labelise(action.status)
  }));

const riskDataGaps = (risk: RiskSourceRecord): string[] => {
  const gaps: string[] = [];

  if (!risk.plainEnglish) {
    gaps.push("Add a plain-English impact summary before sharing this risk.");
  }

  if (!risk.recommendedActions || risk.recommendedActions.length === 0) {
    gaps.push("Add recommended next actions before assigning this risk.");
  }

  if (!risk.sourceRefs || risk.sourceRefs.length === 0) {
    gaps.push("Source references are unavailable for this risk.");
  }

  if (!risk.ruleTraceRefs || risk.ruleTraceRefs.length === 0) {
    gaps.push("Rule-check references are unavailable for this risk.");
  }

  return gaps;
};

const toRiskDisplay = ({
  risk,
  decision,
  projectId
}: {
  risk: RiskSourceRecord;
  decision?: OpenDecisionForDisplay;
  projectId: string;
}): RiskLedgerRiskDisplay => {
  const severity = risk.severity ?? "warning";
  const apartmentTypeId =
    risk.apartmentTypeId ?? risk.scope?.refId ?? "project-risk-unscoped";
  const actions = actionOptions(risk.recommendedActions ?? []);
  const message = humaniseAuthoredText(
    risk.plainEnglish ?? risk.title ?? "Risk message not emitted by the engine."
  );

  return {
    id: risk.id,
    apartmentTypeId,
    apartmentTypeLabel: solutionDisplayName(apartmentTypeId, { projectId }),
    apartmentTypeLabels: [solutionDisplayName(apartmentTypeId, { projectId })],
    affectedLabel: solutionDisplayName(apartmentTypeId, { projectId }),
    title: humaniseAuthoredText(risk.title ?? "Project risk"),
    message,
    severity,
    severityLabel:
      severity === "blocker" ? "Blocker" : severity === "warning" ? "Warning" : "Info",
    type: risk.type ?? "other",
    typeLabel: typeLabel(risk.type ?? "other"),
    status: risk.status,
    statusLabel: labelise(risk.status),
    blocksExport: risk.blocksExport,
    scopeLabel: risk.scope?.label ?? labelise(risk.scope?.refId ?? apartmentTypeId),
    sourceRefCount: risk.sourceRefs?.length ?? 0,
    ruleTraceRefCount: risk.ruleTraceRefs?.length ?? 0,
    technicalTraceCount: risk.technicalTrace?.length ?? 0,
    actionCount: actions.length,
    sourceItemCount: 1,
    actionOptions: actions,
    facets: riskFacetValues(risk, severity),
    ...(decision ? { decisionId: decision.id } : {}),
    ...(decision?.message
      ? { clarificationPrompt: humaniseAuthoredText(decision.message) }
      : {}),
    dataGaps: riskDataGaps(risk)
  };
};

const groupRisks = (risks: readonly RiskLedgerRiskDisplay[]): RiskLedgerRiskGroup[] =>
  severityOrder
    .map((severity) => {
      const items = risks.filter((risk) => risk.severity === severity);
      const meta = severityMeta[severity];

      return {
        id: severity,
        label: meta.label,
        count: items.length,
        accent: meta.accent,
        items
      };
    })
    .filter((group) => group.items.length > 0);

const dedupeRisks = (
  risks: readonly RiskLedgerRiskDisplay[],
  totalApartmentTypeCount: number
): RiskLedgerRiskDisplay[] => {
  const byKey = new Map<string, RiskLedgerRiskDisplay>();

  risks.forEach((risk) => {
    const key = clusterKey(
      [
        risk.severity,
        risk.typeLabel,
        risk.title,
        risk.message,
        String(risk.blocksExport)
      ].join("|")
    );
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, {
        ...risk,
        apartmentTypeLabels: [risk.apartmentTypeLabel],
        affectedLabel: affectedLabel({
          labels: [risk.apartmentTypeLabel],
          totalApartmentTypeCount
        })
      });
      return;
    }

    const apartmentTypeLabels = unique([
      ...existing.apartmentTypeLabels,
      risk.apartmentTypeLabel
    ]);

    byKey.set(key, {
      ...existing,
      sourceItemCount: existing.sourceItemCount + 1,
      apartmentTypeLabels,
      affectedLabel: affectedLabel({
        labels: apartmentTypeLabels,
        totalApartmentTypeCount
      }),
      sourceRefCount: existing.sourceRefCount + risk.sourceRefCount,
      ruleTraceRefCount: existing.ruleTraceRefCount + risk.ruleTraceRefCount,
      technicalTraceCount: existing.technicalTraceCount + risk.technicalTraceCount,
      actionOptions: existing.actionOptions.length
        ? existing.actionOptions
        : risk.actionOptions,
      actionCount: Math.max(existing.actionCount, risk.actionCount),
      facets: mergeRiskFacetValues(existing.facets, risk.facets),
      dataGaps: mergeDataGaps(existing.dataGaps, risk.dataGaps)
    });
  });

  return [...byKey.values()];
};

const assumptionsFromFixture = (
  solutions: readonly ApartmentSolutionForDisplay[],
  projectId: string
): RiskLedgerAssumptionDisplay[] =>
  solutions.flatMap((solution) =>
    solution.justifications.flatMap((justification: SolutionJustification) =>
      justification.assumptions.map((assumption, index) => {
        const targetRef = justification.targetRef ?? solution.apartmentTypeId;

        return {
          id: `assumption-${solution.apartmentTypeId}-${justification.id}-${index}`,
          apartmentTypeId: solution.apartmentTypeId,
          apartmentTypeLabel: solutionDisplayName(solution.apartmentTypeId, {
            projectId
          }),
          apartmentTypeLabels: [
            solutionDisplayName(solution.apartmentTypeId, {
              projectId
            })
          ],
          affectedLabel: solutionDisplayName(solution.apartmentTypeId, {
            projectId
          }),
          targetRef,
          targetLabel: solutionDisplayName(targetRef, { projectId }),
          text: humaniseAuthoredText(assumption),
          sourceItemCount: 1,
          sourceRefCount: justification.sourceRefs?.length ?? 0,
          ruleTraceRefCount: justification.ruleTraceRefs?.length ?? 0,
          dataGaps: []
        };
      })
    )
  );

const dedupeAssumptions = (
  assumptions: readonly RiskLedgerAssumptionDisplay[],
  totalApartmentTypeCount: number
): RiskLedgerAssumptionDisplay[] => {
  const byText = new Map<string, RiskLedgerAssumptionDisplay>();

  assumptions.forEach((assumption) => {
    const key = clusterKey(assumption.text);
    const existing = byText.get(key);

    if (!existing) {
      byText.set(key, {
        ...assumption,
        apartmentTypeLabels: [assumption.apartmentTypeLabel],
        affectedLabel: affectedLabel({
          labels: [assumption.apartmentTypeLabel],
          totalApartmentTypeCount
        })
      });
      return;
    }

    const apartmentTypeLabels = unique([
      ...existing.apartmentTypeLabels,
      assumption.apartmentTypeLabel
    ]);

    byText.set(key, {
      ...existing,
      sourceItemCount: existing.sourceItemCount + 1,
      apartmentTypeLabels,
      affectedLabel: affectedLabel({
        labels: apartmentTypeLabels,
        totalApartmentTypeCount
      }),
      targetLabel:
        existing.targetLabel === assumption.targetLabel
          ? existing.targetLabel
          : "Multiple targets",
      sourceRefCount: existing.sourceRefCount + assumption.sourceRefCount,
      ruleTraceRefCount: existing.ruleTraceRefCount + assumption.ruleTraceRefCount,
      dataGaps: mergeDataGaps(existing.dataGaps, assumption.dataGaps)
    });
  });

  return [...byText.values()];
};

const clarificationTitleFromQuestion = (question: string): string => {
  const trimmed = question.trim();

  if (!trimmed) {
    return "Question for the client";
  }

  const questionEnd = trimmed.indexOf("?");

  if (questionEnd >= 0) {
    return trimmed.slice(0, questionEnd + 1);
  }

  return trimmed.length > 120 ? `${trimmed.slice(0, 117).trimEnd()}...` : trimmed;
};

const lowConfidenceParts = (
  question: string
): { confidence: string; threshold: string } | undefined => {
  const match = question.match(
    /requirement candidate confidence ([0-9.]+) is below the ([0-9.]+) approval threshold/i
  );

  if (!match?.[1] || !match[2]) {
    return undefined;
  }

  return {
    confidence: match[1],
    threshold: match[2]
  };
};

const clarificationFromDecision = ({
  decision,
  risk
}: {
  decision: OpenDecisionForDisplay;
  risk?: RiskLedgerRiskDisplay;
}): RiskLedgerClarificationDisplay => {
  const subjectRef = decision.subjectRef ?? decision.id;
  const question = humaniseAuthoredText(
    decision.message ??
      risk?.clarificationPrompt ??
      "Open decision question is unavailable."
  );

  return {
    id: decision.id,
    stage: decision.stage,
    stageLabel: stageLabel(decision.stage),
    subjectRef,
    subjectRefs: [subjectRef],
    title: clarificationTitleFromQuestion(question),
    question,
    affectedLabel: risk?.apartmentTypeLabel ?? "Whole job",
    sourceItemCount: 1,
    ...(risk
      ? { linkedRiskId: risk.id, apartmentTypeLabel: risk.apartmentTypeLabel }
      : {}),
    source: risk ? "needsUserDecision" : "openDecision",
    notYetProduced: "Turn this question into client-ready wording after review."
  };
};

const dedupeClarifications = (
  clarifications: readonly RiskLedgerClarificationDisplay[]
): RiskLedgerClarificationDisplay[] => {
  const byQuestion = new Map<string, RiskLedgerClarificationDisplay>();

  clarifications.forEach((clarification) => {
    const key = clusterKey(
      [clarification.stage, clarification.title, clarification.question].join("|")
    );
    const existing = byQuestion.get(key);

    if (!existing) {
      byQuestion.set(key, clarification);
      return;
    }

    const linkedRiskId = existing.linkedRiskId ?? clarification.linkedRiskId;
    const apartmentTypeLabel =
      existing.apartmentTypeLabel ?? clarification.apartmentTypeLabel;

    byQuestion.set(key, {
      ...existing,
      sourceItemCount: existing.sourceItemCount + 1,
      subjectRefs: unique([...existing.subjectRefs, ...clarification.subjectRefs]),
      affectedLabel:
        existing.affectedLabel === clarification.affectedLabel
          ? existing.affectedLabel
          : "Multiple references",
      ...(linkedRiskId ? { linkedRiskId } : {}),
      ...(apartmentTypeLabel ? { apartmentTypeLabel } : {})
    });
  });

  return [...byQuestion.values()].map((clarification) => {
    const confidence = lowConfidenceParts(clarification.question);

    if (!confidence || clarification.sourceItemCount === 1) {
      return clarification;
    }

    return {
      ...clarification,
      title: "Review low-confidence requirement candidates",
      question: `${clarification.sourceItemCount} requirement candidates have confidence ${confidence.confidence}, below the ${confidence.threshold} approval threshold.`,
      affectedLabel: "Requirement candidates"
    };
  });
};

const countBySeverity = (
  risks: readonly RiskLedgerRiskDisplay[]
): Record<RiskSeverity, number> => ({
  blocker: risks.filter((risk) => risk.severity === "blocker").length,
  warning: risks.filter((risk) => risk.severity === "warning").length,
  info: risks.filter((risk) => risk.severity === "info").length
});

const countByStage = (
  clarifications: readonly RiskLedgerClarificationDisplay[]
): Record<OpenDecisionStage, number> => ({
  "lane-a": clarifications.filter((clarification) => clarification.stage === "lane-a")
    .length,
  "lane-b": clarifications.filter((clarification) => clarification.stage === "lane-b")
    .length,
  output: clarifications.filter((clarification) => clarification.stage === "output")
    .length
});

const exclusionFromScopeRow = (row: ProjectScopeRow): RiskLedgerExclusionDisplay => ({
  id: `exclusion-${row.id}`,
  title: humaniseAuthoredText(row.item),
  message: humaniseAuthoredText(row.rationale),
  status: row.status === "by_others" ? "by_others" : "excluded",
  statusLabel: row.status === "by_others" ? "By others" : "Excluded",
  affectedLabel: "Whole job",
  sourceItemCount: 1,
  sourceRefCount: row.sourceRefCount ?? row.sourceRefs?.length ?? 0,
  ruleTraceRefCount: row.ruleTraceRefCount ?? row.ruleTraceRefs?.length ?? 0
});

const dedupeExclusions = (
  rows: readonly ProjectScopeRow[],
  totalApartmentTypeCount: number
): RiskLedgerExclusionDisplay[] => {
  const byText = new Map<string, RiskLedgerExclusionDisplay>();

  rows
    .filter((row) => row.status === "excluded" || row.status === "by_others")
    .map(exclusionFromScopeRow)
    .forEach((exclusion) => {
      const key = clusterKey([exclusion.title, exclusion.message].join("|"));
      const existing = byText.get(key);

      if (!existing) {
        byText.set(key, exclusion);
        return;
      }

      const sourceItemCount = existing.sourceItemCount + 1;

      byText.set(key, {
        ...existing,
        sourceItemCount,
        affectedLabel:
          totalApartmentTypeCount > 0 && sourceItemCount >= totalApartmentTypeCount
            ? "All types"
            : `${sourceItemCount} scope rows`,
        sourceRefCount: existing.sourceRefCount + exclusion.sourceRefCount,
        ruleTraceRefCount: existing.ruleTraceRefCount + exclusion.ruleTraceRefCount
      });
    });

  return [...byText.values()].map((exclusion) => ({
    ...exclusion,
    affectedLabel:
      totalApartmentTypeCount > 0 &&
      exclusion.sourceItemCount >= totalApartmentTypeCount
        ? "All types"
        : exclusion.affectedLabel
  }));
};

const ledgerGroupsFor = ({
  risks,
  assumptions,
  clarifications,
  exclusions
}: {
  risks: readonly RiskLedgerRiskDisplay[];
  assumptions: readonly RiskLedgerAssumptionDisplay[];
  clarifications: readonly RiskLedgerClarificationDisplay[];
  exclusions: readonly RiskLedgerExclusionDisplay[];
}): RiskLedgerSectionSummary[] => {
  const input: {
    id: RiskLedgerSectionId;
    items: readonly { sourceItemCount: number }[];
  }[] = [
    { id: "risks", items: risks },
    { id: "assumptions", items: assumptions },
    { id: "clarifications", items: clarifications },
    { id: "exclusions", items: exclusions }
  ];

  return input.map(({ id, items }) => ({
    id,
    label: sectionMeta[id].label,
    count: items.length,
    sourceItemCount: items.reduce((sum, item) => sum + item.sourceItemCount, 0),
    accent: sectionMeta[id].accent
  }));
};

export const projectFixtureToRiskLedgerView = (
  fixture: ProjectFixtureForDisplay
): RiskLedgerDisplayViewModel => {
  const solutionRiskById = new Map(
    fixture.apartmentSolutions
      .flatMap((solution) =>
        solution.risks.map((risk) => riskRecordFromSolution({ solution, risk }))
      )
      .map((risk) => [risk.id, risk])
  );
  const projectOpenRiskIds = fixture.risks
    .filter((risk) => risk.status === "open")
    .map((risk) => risk.id);
  const summaryRiskById = new Map(
    fixture.risks.map((risk) => [risk.id, risk as ProjectRiskSummary])
  );
  const needsUserDecisionRiskIds = new Set([
    ...[...solutionRiskById.values()]
      .filter((risk) => risk.type === "needsUserDecision")
      .map((risk) => risk.id),
    ...fixture.risks
      .filter((risk) => risk.type === "needsUserDecision")
      .map((risk) => risk.id)
  ]);
  const isInternalDecisionRisk = (id: string): boolean => {
    const detailed = solutionRiskById.get(id);
    const summary = summaryRiskById.get(id);

    return (
      needsUserDecisionRiskIds.has(id) ||
      detailed?.type === "needsUserDecision" ||
      summary?.type === "needsUserDecision"
    );
  };
  const authoritativeRiskIds =
    projectOpenRiskIds.length > 0
      ? projectOpenRiskIds
      : [...solutionRiskById.values()]
          .filter((risk) => risk.status === "open")
          .map((risk) => risk.id);
  const decisionBySubjectRef = new Map(
    fixture.openDecisions
      .filter((decision) => decision.subjectRef)
      .map((decision) => [
        decision.subjectRef as string,
        decision as OpenDecisionForDisplay
      ])
  );
  const totalApartmentTypeCount = fixture.takeoffRows.length;
  const rawRisks = authoritativeRiskIds
    .map((id) => {
      const decision = decisionBySubjectRef.get(id);

      return toRiskDisplay({
        risk:
          solutionRiskById.get(id) ??
          riskRecordFromSummary(
            summaryRiskById.get(id) ?? {
              id,
              status: "open",
              blocksExport: false
            }
          ),
        ...(decision ? { decision } : {}),
        projectId: fixture.project.projectId
      });
    })
    .sort((left, right) => {
      const severityDelta =
        severityOrder.indexOf(left.severity) - severityOrder.indexOf(right.severity);

      if (severityDelta !== 0) {
        return severityDelta;
      }

      return `${left.apartmentTypeLabel}-${left.title}`.localeCompare(
        `${right.apartmentTypeLabel}-${right.title}`
      );
    });
  const risks = dedupeRisks(rawRisks, totalApartmentTypeCount);
  const riskById = new Map(risks.map((risk) => [risk.id, risk]));
  const rawAssumptions = assumptionsFromFixture(
    fixture.apartmentSolutions,
    fixture.project.projectId
  );
  const assumptions = dedupeAssumptions(rawAssumptions, totalApartmentTypeCount);
  const rawClarifications = fixture.openDecisions
    .filter(
      (decision) =>
        decision.stage !== "lane-b" ||
        !decision.subjectRef ||
        !needsUserDecisionRiskIds.has(decision.subjectRef)
    )
    .map((decision) => {
      const displayDecision = decision as OpenDecisionForDisplay;
      const risk = displayDecision.subjectRef
        ? riskById.get(displayDecision.subjectRef)
        : undefined;

      return clarificationFromDecision({
        decision: displayDecision,
        ...(risk ? { risk } : {})
      });
    });
  const clarifications = dedupeClarifications(rawClarifications);
  const exclusions = dedupeExclusions(fixture.scopeMatrixRows, totalApartmentTypeCount);
  const ledgerGroups = ledgerGroupsFor({
    risks,
    assumptions,
    clarifications,
    exclusions
  });
  const dedupedRowCount = ledgerGroups.reduce((sum, group) => sum + group.count, 0);
  const rawLedgerItemCount =
    rawRisks.length +
    rawAssumptions.length +
    rawClarifications.length +
    exclusions.reduce((sum, exclusion) => sum + exclusion.sourceItemCount, 0);
  const severityCounts = countBySeverity(risks);
  const clarificationStageCounts = countByStage(clarifications);
  const missingDetailedRiskCount = authoritativeRiskIds.filter(
    (id) => !solutionRiskById.has(id)
  ).length;
  const dataGaps = [
    "Decision-linked risks are clustered here; detailed answer choices remain in the Decisions hub.",
    "Clarifications are grouped from open decisions that still need a client question.",
    ...(missingDetailedRiskCount > 0
      ? [`${missingDetailedRiskCount} project risk(s) only have summary-level detail.`]
      : [])
  ];
  const core = RiskLedgerViewSchema.parse({
    projectId: fixture.project.projectId,
    status: viewStatus(fixture.project.outputBundle.status),
    riskIds: risks.map((risk) => risk.id),
    blockerCount: severityCounts.blocker,
    needsUserDecisionCount: needsUserDecisionRiskIds.size,
    selectedRiskId: risks[0]?.id,
    updatedAt: fixture.fixture.generatedAt,
    openRiskCount: risks.length,
    assumptionCount: assumptions.length,
    clarificationCount: clarifications.length,
    dataGaps
  });

  return {
    ...core,
    risks,
    riskGroups: groupRisks(risks),
    assumptions,
    clarifications,
    exclusions,
    ledgerGroups,
    dedupedRowCount,
    rawLedgerItemCount,
    severityCounts,
    clarificationStageCounts,
    generatedFrom: fixture.fixture.generatedFrom
  };
};
